import { useState, useEffect, useMemo, useCallback } from 'react';
import { SymbolOptions } from 'milsymbol';
import { buildSidc, parseSidc, getSubordinateEchelon } from '../SidcBuilder/sidcOptions';
import { getAffiliationSidcDigit } from '../../../types/orbat';
import { UnitModalContext, UnitModalMode, EditableUnit, ParentUnitRef } from './UnitModal.types';

interface UseUnitModalStateProps {
    /** Whether the modal is visible */
    show: boolean;
    /** Create or edit mode */
    mode: UnitModalMode;
    /** Modal context — gates ORBAT-only fields like `isPubliclyJoinable` */
    context: UnitModalContext;
    /** Unit being edited (for edit mode) */
    editingUnit?: EditableUnit | null;
    /** Faction affiliation name for identity locking */
    factionAffiliationName?: string;
    /** Parent unit for hierarchy */
    parentUnit?: ParentUnitRef | null;
    /** Whether identity is locked to faction */
    lockIdentity?: boolean;
}

interface UseUnitModalStateReturn {
    // State values
    name: string;
    sidc: string;
    markerProperties: SymbolOptions;
    validated: boolean;
    isPubliclyJoinable: boolean;

    // State setters
    setName: (name: string) => void;
    setSidc: (sidc: string) => void;
    setMarkerProperties: (props: SymbolOptions) => void;
    setValidated: (validated: boolean) => void;
    setIsPubliclyJoinable: (value: boolean) => void;

    // Computed values
    defaultIdentity: string;
    defaultEchelon: string;
    lockedIdentityValue: string | undefined;
    parentEchelonValue: string | undefined;

    // Actions
    resetForm: () => void;
}

const DEFAULT_SIDC = '10031000151211000000'; // Friendly Infantry Company

/**
 * Hook for managing UnitModal form state
 * Handles SIDC building, identity locking, and subordinate echelon calculation
 */
export function useUnitModalState({
    show,
    mode,
    context,
    editingUnit,
    factionAffiliationName,
    parentUnit,
    lockIdentity = false,
}: UseUnitModalStateProps): UseUnitModalStateReturn {
    const [name, setName] = useState('');
    const [sidc, setSidc] = useState(DEFAULT_SIDC);
    const [markerProperties, setMarkerProperties] = useState<SymbolOptions>({});
    const [validated, setValidated] = useState(false);
    const [isPubliclyJoinable, setIsPubliclyJoinable] = useState(false);

    // Get identity digit from faction affiliation name
    const defaultIdentity = useMemo(() => {
        return factionAffiliationName
            ? getAffiliationSidcDigit(factionAffiliationName)
            : '3'; // Default to friendly
    }, [factionAffiliationName]);

    // Get subordinate echelon when creating under a parent unit
    const defaultEchelon = useMemo(() => {
        if (parentUnit && mode === 'create') {
            const parentParts = parseSidc(parentUnit.sidc);
            return getSubordinateEchelon(parentParts.echelon);
        }
        return '15'; // Default to Company
    }, [parentUnit, mode]);

    // Locked identity value (only when lockIdentity is true)
    const lockedIdentityValue = lockIdentity ? defaultIdentity : undefined;

    // Extract parent unit's echelon for filtering subordinate echelon options
    const parentEchelonValue = useMemo(() => {
        if (parentUnit) {
            const parentParts = parseSidc(parentUnit.sidc);
            return parentParts.echelon;
        }
        return undefined;
    }, [parentUnit, mode]);

    // Reset form when modal opens
    useEffect(() => {
        if (show) {
            if (mode === 'edit' && editingUnit) {
                // Populate form with existing unit data
                setName(editingUnit.name || '');

                // If identity is locked, override the editing unit's identity with faction affiliation
                if (lockIdentity) {
                    const editParts = parseSidc(editingUnit.sidc);
                    const newSidc = buildSidc(
                        defaultIdentity,
                        editParts.symbolSet,
                        editParts.status,
                        editParts.hqtfd,
                        editParts.echelon,
                        editParts.unitType
                    );
                    setSidc(newSidc);
                } else {
                    setSidc(editingUnit.sidc);
                }

                // Seed text modifiers so they pre-populate in the SidcBuilder
                // form. Strip the synthetic `dtg` (derived from position_time
                // at display time) — it must not appear in the editable form.
                const { dtg: _dtg, ...textModifiers } = (editingUnit.markerProperties ?? {}) as Record<string, unknown>;
                setMarkerProperties(textModifiers as SymbolOptions);

                // `isPubliclyJoinable` is an ORBAT-only concept. In other
                // contexts (e.g. Map) the modal must not couple itself to
                // this field — always reset to `false`.
                if (context === 'orbat') {
                    setIsPubliclyJoinable(editingUnit.isPubliclyJoinable ?? false);
                } else {
                    setIsPubliclyJoinable(false);
                }
            } else {
                // Create mode - reset to defaults
                setName('');
                // Build initial SIDC with faction's affiliation and appropriate echelon
                const initialSidc = buildSidc(
                    defaultIdentity,
                    '10', // Land Unit
                    '0',  // Present
                    '0',  // None
                    defaultEchelon,
                    '1211000000' // Infantry
                );
                setSidc(initialSidc);
                setMarkerProperties({});
                setIsPubliclyJoinable(false);
            }
            setValidated(false);
        }
    }, [show, defaultIdentity, defaultEchelon, mode, editingUnit, lockIdentity, context]);

    // Reset form to defaults
    const resetForm = useCallback(() => {
        setName('');
        setSidc(DEFAULT_SIDC);
        setMarkerProperties({});
        setValidated(false);
        setIsPubliclyJoinable(false);
    }, []);

    return {
        // State values
        name,
        sidc,
        markerProperties,
        validated,
        isPubliclyJoinable,

        // State setters
        setName,
        setSidc,
        setMarkerProperties,
        setValidated,
        setIsPubliclyJoinable,

        // Computed values
        defaultIdentity,
        defaultEchelon,
        lockedIdentityValue,
        parentEchelonValue,

        // Actions
        resetForm,
    };
}
